import React, { useContext } from 'react';
import { usePointedTime } from 'utils/components/TimeSeriesChart/timepoint';
import AppContext from 'app/AppContext';

import classNames from 'classnames';
import {
    PortfolioPlDataItem,
    deriveLegendData,
} from 'widgets/portfolioPl/portfolioPlData';
import { getPnLLegendLabelByBenchmark, totalPnLLabel } from 'utils/PnL';

import './PortfolioPlPerformanceChartLegends.scss';
import { PortfolioPlDimensions } from 'widgets/portfolioPl/dimensions';
import { usePortfolioPlConfigSlice } from 'widgets/portfolioPl/store/config/configSlice';
import {
    realisedTooltip,
    totalTooltip,
    unrealisedTooltip,
    indexTooltip,
} from 'widgets/portfolioPl/chart/performance/help/portfolioPlPerformanceChartLegendTooltips';
import PortfolioPlPerformanceChartHelp from 'widgets/portfolioPl/chart/performance/help/PortfolioPlPerformanceChartHelp';
import { WidgetTimeSeriesLegend } from 'widgets/WidgetTimeSeriesLegend/WidgetTimeSeriesLegend';
import { PortfolioPlWidgetLegends } from 'widgets/portfolioPl/store/config/PortfolioPlWidgetConfigState';
import { usePortfolioPlLegendStatus } from 'widgets/portfolioPl/chart/hooks';

export interface PortfolioPlPerformanceChartLegendsProps {
    containerLeft: number;
    data: PortfolioPlDataItem[];
    dimensions: PortfolioPlDimensions;
}

export function PortfolioPlPerformanceChartLegends(
    props: PortfolioPlPerformanceChartLegendsProps
) {
    const { containerLeft, data, dimensions } = props;
    const time = usePointedTime('PortfolioPlPerformanceChartLegends');
    const { numberFormat, currencyFormat } = useContext(AppContext);

    const { config } = usePortfolioPlConfigSlice();

    const { benchmarkType, index } = config;

    const { hidePerformanceLegends } = usePortfolioPlLegendStatus();
    if (hidePerformanceLegends) {
        return <></>;
    }

    const { realizedPnl, unrealizedPnl, totalPnl, indexPnl } = deriveLegendData(
        time,
        data,
        numberFormat,
        currencyFormat
    );
    const realizedLabel = getPnLLegendLabelByBenchmark(
        'realized',
        benchmarkType
    );
    const unrealizedLabel = getPnLLegendLabelByBenchmark(
        'unrealized',
        benchmarkType
    );

    const {
        performanceChart: { legendsMarginTop },
    } = dimensions;

    return (
        <div
            className={classNames(
                'PortfolioPlPerformanceChartLegends',
                'legend-group',
                index && 'with-index'
            )}
            style={{
                marginTop: legendsMarginTop + 'px',
            }}
        >
            <WidgetTimeSeriesLegend<PortfolioPlWidgetLegends>
                className={classNames(
                    'PortfolioPlPerformanceChartLegend',
                    'realized'
                )}
                label={realizedLabel}
                value={realizedPnl}
                hideSeriesPropertyName={'hideRealizedPnl'}
                containerLeft={containerLeft}
                twoColourBorder={true}
                tooltip={realisedTooltip}
            />
            <WidgetTimeSeriesLegend<PortfolioPlWidgetLegends>
                className={classNames(
                    'PortfolioPlPerformanceChartLegend',
                    'unrealized'
                )}
                label={unrealizedLabel}
                value={unrealizedPnl}
                hideSeriesPropertyName={'hideUnrealizedPnl'}
                containerLeft={containerLeft}
                tooltip={unrealisedTooltip}
            />
            <WidgetTimeSeriesLegend<PortfolioPlWidgetLegends>
                alignDropdown={'right'}
                className={classNames(
                    'PortfolioPlPerformanceChartLegend',
                    'total'
                )}
                hideSeriesPropertyName={'hideTotalPnl'}
                label={totalPnLLabel}
                value={totalPnl}
                containerLeft={containerLeft}
                tooltip={totalTooltip}
            />
            {index && (
                <WidgetTimeSeriesLegend<PortfolioPlWidgetLegends>
                    alignDropdown={'right'}
                    className={classNames(
                        'PortfolioPlPerformanceChartLegend',
                        'index'
                    )}
                    hideSeriesPropertyName={'hideIndexPnl'}
                    label={index}
                    value={indexPnl}
                    containerLeft={containerLeft}
                    tooltip={indexTooltip}
                />
            )}

            <PortfolioPlPerformanceChartHelp />
        </div>
    );
}
